import React, { useState, useEffect } from 'react';
import { AlignLeft, ListTodo, Plus, Trash2, Square, CheckSquare, Info } from 'lucide-react';

interface TaskItem {
    id: string;
    text: string;
    completed: boolean;
}

interface TaskBodyProps {
    data: any;
    updatePayload: (updates: any) => void;
}

/**
 * TaskBody: Polymorphic editor for single notes or interactive checklists.
 * SHADOW VERSION (.tsx)
 */
export const TaskBody: React.FC<TaskBodyProps> = ({ data, updatePayload }) => {
    const { payload = {} } = data;
    const mode = payload.mode || 'notes'; // 'notes' | 'checklist'
    const items = (payload.items as TaskItem[]) || [];
    
    const [localNotes, setLocalNotes] = useState<string>(payload.notes || "");
    const [newItemText, setNewItemText] = useState<string>("");

    useEffect(() => {
        setLocalNotes(payload.notes || "");
    }, [payload.notes]);

    const toggleMode = (newMode: 'notes' | 'checklist') => updatePayload({ mode: newMode });

    const handleAddItem = (e?: React.KeyboardEvent) => {
        if (e && e.key !== 'Enter') return;
        if (!newItemText.trim()) return;
        
        const newItem: TaskItem = {
            id: crypto.randomUUID(),
            text: newItemText.trim(),
            completed: false
        };
        updatePayload({ items: [...items, newItem] });
        setNewItemText("");
    };

    const toggleItem = (id: string) => {
        const newItems = items.map(item => 
            item.id === id ? { ...item, completed: !item.completed } : item
        );
        updatePayload({ items: newItems });
    };

    const deleteItem = (id: string) => {
        updatePayload({ items: items.filter(item => item.id !== id) });
    };

    const sectionStyle: React.CSSProperties = { marginBottom: '20px' };
    const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: '900', color: '#444', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '6px' };
    const inputStyle: React.CSSProperties = { all: 'unset', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid #222', borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', width: '100%', boxSizing: 'border-box' };

    return (
        <div className="animate-in fade-in slide-in-from-top-2">
            {/* 1. MODE TOGGLE */}
            <div style={{ display: 'flex', backgroundColor: '#050505', padding: '3px', borderRadius: '10px', border: '1px solid #1a1a1a', marginBottom: '25px', gap: '3px' }}>
                <button 
                    onClick={() => toggleMode('notes')}
                    style={{ all: 'unset', flex: 1, padding: '10px 0', borderRadius: '8px', backgroundColor: mode === 'notes' ? 'rgba(255,255,255,0.05)' : 'transparent', border: mode === 'notes' ? '1px solid #333' : '1px solid transparent', color: mode === 'notes' ? '#fff' : '#444', fontSize: '10px', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                >
                    <AlignLeft size={14} /> DESCRIPTION
                </button>
                <button 
                    onClick={() => toggleMode('checklist')}
                    style={{ all: 'unset', flex: 1, padding: '10px 0', borderRadius: '8px', backgroundColor: mode === 'checklist' ? 'rgba(255,255,255,0.05)' : 'transparent', border: mode === 'checklist' ? '1px solid #333' : '1px solid transparent', color: mode === 'checklist' ? '#fff' : '#444', fontSize: '10px', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                >
                    <ListTodo size={14} /> CHECKLIST
                </button>
            </div>

            {/* 2. CONTENT AREA */}
            {mode === 'notes' ? (
                <div style={sectionStyle}>
                    <div style={labelStyle}><Info size={12} /> Detailed Notes</div>
                    <textarea 
                        placeholder="Add additional tactical context here..."
                        value={localNotes}
                        onChange={(e) => setLocalNotes(e.target.value)}
                        onBlur={() => updatePayload({ notes: localNotes })}
                        style={{ ...inputStyle, height: '200px', resize: 'none', fontSize: '13px', lineHeight: '1.6' }}
                    />
                </div>
            ) : (
                <div style={sectionStyle}>
                    <div style={labelStyle}><ListTodo size={12} /> Tactical Items</div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                        {items.length === 0 && (
                            <div style={{ padding: '30px 20px', textAlign: 'center', border: '1px dashed #222', borderRadius: '12px', color: '#444', fontSize: '12px' }}>
                                Your list is currently empty.
                            </div>
                        )}
                        {items.map(item => (
                            <div 
                                key={item.id} 
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', 
                                    backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '10px', 
                                    border: `1px solid ${item.completed ? '#1a1a1a' : '#222'}`,
                                    opacity: item.completed ? 0.5 : 1,
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <button onClick={() => toggleItem(item.id)} style={{ all: 'unset', cursor: 'pointer' }}>
                                    {item.completed ? <CheckSquare size={20} color="#2ecc71" /> : <Square size={20} color="#444" />}
                                </button>
                                <span style={{ flex: 1, fontSize: '14px', color: '#fff', textDecoration: item.completed ? 'line-through' : 'none', fontWeight: '600' }}>
                                    {item.text}
                                </span>
                                <button onClick={() => deleteItem(item.id)} style={{ all: 'unset', padding: '4px', cursor: 'pointer', opacity: 0.3 }}>
                                    <Trash2 size={16} color="#ef4444" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* QUICK ADD INPUT */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input 
                            type="text"
                            placeholder="Add item..."
                            value={newItemText}
                            onChange={(e) => setNewItemText(e.target.value)}
                            onKeyDown={handleAddItem}
                            style={{ ...inputStyle, flex: 1, height: '44px' }}
                        />
                        <button 
                            onClick={() => handleAddItem()}
                            style={{ all: 'unset', width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#f29b11', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                            <Plus size={20} color="#000" strokeWidth={3} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
